import React from "react";
import { Edit2, Trash2 } from "lucide-react";
import NumberFieldAddon from "../NumberFieldAddon";
import { calcularImporteLibreria, calcularImporteVenta } from "../../utils/inventory";

const TAB_OPTIONS = [
  { id: "envio", label: "Enviar a Librería" },
  { id: "devolucion", label: "Devolución" },
  { id: "venta", label: "Venta" }
];

export default function OperacionesTab({
  operacionForm,
  setOperacionForm,
  operacionTab,
  setOperacionTab,
  librerias,
  libros,
  getStock,
  historial,
  onRegistrarOperacion,
  onEditarOperacion,
  onEliminarOperacion,
  onCancelarEdicion
}) {
  const isVenta = operacionTab === "venta";
  const isEditingStock = Boolean(operacionForm.id) && operacionTab !== "venta";

  return (
    <div className="space-y-6 max-w-5xl">
      <h2 className="text-2xl font-bold text-gray-800">Movimientos y Operaciones</h2>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <p className="text-sm font-medium text-gray-700 mb-3">Tipo de operación</p>
        <div className="flex gap-2 flex-wrap">
          {TAB_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setOperacionTab(opt.id)}
              className={`px-4 py-2 rounded-lg text-sm border transition-colors ${operacionTab === opt.id ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-medium mb-6 text-indigo-900 border-b pb-2">{isEditingStock || (isVenta && operacionForm.id) ? "Editar movimiento" : `Registrar ${TAB_OPTIONS.find((t) => t.id === operacionTab)?.label || "Operación"}`}</h3>
        <form onSubmit={onRegistrarOperacion} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col space-y-1 md:col-span-2"><label className="text-sm font-medium text-gray-600">Librería</label><select required value={operacionForm.libreriaId} onChange={(e) => setOperacionForm({ ...operacionForm, libreriaId: e.target.value })} className="p-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none w-full"><option value="">Selecciona una librería...</option>{librerias.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}</select></div>
            <div className="flex flex-col space-y-1"><label className="text-sm font-medium text-gray-600">Cantidad</label><input type="number" min="1" required value={operacionForm.cantidad} onChange={(e) => setOperacionForm({ ...operacionForm, cantidad: e.target.value })} placeholder="Ej: 10" className="p-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none w-full" /></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col space-y-1 md:col-span-2"><label className="text-sm font-medium text-gray-600">Libro</label><select required value={operacionForm.libroId} onChange={(e) => setOperacionForm({ ...operacionForm, libroId: e.target.value })} className="p-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none w-full"><option value="">Selecciona un libro...</option>{libros.map((l) => { const stockActual = operacionForm.libreriaId ? getStock(l.id, operacionForm.libreriaId) : 0; return <option key={l.id} value={l.id}>{l.titulo} {operacionForm.libreriaId ? `(Stock: ${stockActual})` : ""}</option>; })}</select></div>
            <div className="flex flex-col space-y-1"><label className="text-sm font-medium text-gray-600">Fecha</label><input type="date" required value={operacionForm.fecha} onChange={(e) => setOperacionForm({ ...operacionForm, fecha: e.target.value })} className="p-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none w-full" /></div>
          </div>

          {isVenta && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col space-y-1">
                <label className="text-sm font-medium text-gray-600">Precio de venta</label>
                <NumberFieldAddon
                  addon="$"
                  addonPosition="left"
                  min="0"
                  step="0.01"
                  required
                  placeholder="Precio por ejemplar"
                  value={operacionForm.precioUnitario}
                  onChange={(e) => setOperacionForm({ ...operacionForm, precioUnitario: e.target.value })}
                />
              </div>
              <div className="flex items-end pb-3 md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(operacionForm.cortesia)}
                    onChange={(e) => setOperacionForm({ ...operacionForm, cortesia: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400"
                  />
                  Es cortesía (amigo, club o autor) — solo a modo informativo, no cambia el precio ni la comisión
                </label>
              </div>
            </div>
          )}

          {isVenta && operacionForm.libroId && operacionForm.libreriaId && operacionForm.cantidad && operacionForm.precioUnitario && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mt-4 text-sm text-blue-800">
              <p className="font-semibold mb-1">Resumen de la Venta a Registrar:</p>
              {(() => {
                const librer = librerias.find((l) => String(l.id) === String(operacionForm.libreriaId));
                if (!librer) return null;
                const cant = parseInt(operacionForm.cantidad, 10) || 0;
                const precio = Number(operacionForm.precioUnitario) || 0;
                const base = precio * cant;
                const importeLibreria = calcularImporteLibreria(precio, librer.descuento, cant);
                const importeUsuario = calcularImporteVenta(precio, librer.descuento, cant);
                return (
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Total de la venta: ${base.toFixed(2)}</li>
                    <li>Para la librería ({librer.descuento}%): ${importeLibreria.toFixed(2)}</li>
                    <li className="font-bold pt-1">Para vos: ${importeUsuario.toFixed(2)}</li>
                  </ul>
                );
              })()}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            {(isEditingStock || (isVenta && operacionForm.id)) && <button type="button" onClick={onCancelarEdicion} className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700">Cancelar</button>}
            <button type="submit" className={`px-8 py-3 text-white font-medium rounded-lg shadow-sm transition-colors ${isVenta ? "bg-green-600 hover:bg-green-700" : operacionTab === "devolucion" ? "bg-orange-600 hover:bg-orange-700" : "bg-indigo-600 hover:bg-indigo-700"}`}>{operacionForm.id ? "Guardar cambios" : isVenta ? "Registrar Venta" : operacionTab === "devolucion" ? "Registrar Devolución" : "Registrar Envío"}</button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-gray-50/50"><h3 className="text-lg font-semibold text-gray-800">Historial: {TAB_OPTIONS.find((t) => t.id === operacionTab)?.label}</h3></div>
        <div className="p-0 overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-sm border-b">
                <th className="p-4 font-medium">Fecha</th>
                <th className="p-4 font-medium">Librería</th>
                <th className="p-4 font-medium">Libro</th>
                <th className="p-4 font-medium text-center">Cantidad</th>
                {isVenta && <th className="p-4 font-medium text-right">Precio Unit.</th>}
                {isVenta && <th className="p-4 font-medium text-right">A Facturar</th>}
                {isVenta && <th className="p-4 font-medium text-center">Cortesía</th>}
                <th className="p-4 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {historial.map((item) => (
                <tr key={`${operacionTab}-${item.id}`} className="hover:bg-gray-50/50">
                  <td className="p-4 text-sm text-gray-700">{item.fecha || "-"}</td>
                  <td className="p-4 text-sm text-gray-700">{item.libreriaNombre || "Desconocida"}</td>
                  <td className="p-4 text-sm text-gray-700">{item.libroTitulo || "Desconocido"}</td>
                  <td className="p-4 text-sm text-center font-semibold text-gray-800">{item.cantidad}</td>
                  {isVenta && <td className="p-4 text-sm text-right text-gray-700">${Number(item.precioUnitario || 0).toFixed(2)}</td>}
                  {isVenta && <td className="p-4 text-sm text-right font-semibold text-gray-800">${Number(item.importe || 0).toFixed(2)}</td>}
                  {isVenta && (
                    <td className="p-4 text-center">
                      {item.cortesia && <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Cortesía</span>}
                    </td>
                  )}
                  <td className="p-4 text-right space-x-2">
                    <button onClick={() => onEditarOperacion(item)} className="p-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100" title="Editar"><Edit2 size={16} /></button>
                    <button onClick={() => onEliminarOperacion(item.id)} className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100" title="Eliminar"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {historial.length === 0 && <tr><td colSpan={isVenta ? "8" : "5"} className="p-4 text-center text-gray-500 text-sm">No hay registros para este tipo.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
